import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Container,
  Card,
  Image
} from 'react-bootstrap';
import { OrderContext } from '../../context/OrderContext';
import { useNavigate } from 'react-router-dom';
import './return-summary-styles.css';
import { MdOutlineContactSupport } from 'react-icons/md';
import { RiShoppingBasketLine } from 'react-icons/ri';
import { BsInfoCircle } from 'react-icons/bs';
import { QRCodeSVG } from 'qrcode.react';
import useCreateRefund from '../../hook/useCreateRefund';

const ReturnSummary = () => {
  const {
    refundProduct,
    refundShipment,
    orderNumber,
    email,
    orderProduct
  } = useContext(OrderContext);
  const { dataProducts } = useCreateRefund();

  const map_product = orderProduct?.OrderProducts || [];
  const [filteredProducts, setFilteredProducts] = useState([]);
  const navigate = useNavigate();

  // Veri çek (Strict Mode'da iki kez mount olsa bile sadece bir kez çalışsın)
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (fetchedRef.current) {
      return;
    }
    if (orderNumber && email) {
      fetchedRef.current = true;
      dataProducts(orderNumber, email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber, email]);

  // Tarayıcının geri tuşu ile kolay iade akışında geriye dönmeyi engelle
  useEffect(() => {
    const handlePopState = (event) => {
      // Kullanıcı geri gitmeye çalışsa bile tekrar bu sayfaya yönlendir
      navigate('/return-summary', { replace: true });
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  // “İade Talebi” ürünlerini filtrele ve requested_quantity ekle
  useEffect(() => {
    if (Array.isArray(refundProduct)) {
      // Tüm "İade Talebi" claim'lerinden product'ları topla
      const refundClaims = refundProduct.filter((c) => c.type === 'İade Talebi');
      const allProducts = refundClaims.flatMap(claim => claim.SubjectOrderProducts || []);
      
      // Her product için tüm claim'lerde requested_quantity ara
      const items = allProducts.map((product) => {
        let requestedQty = null;
        // Tüm claim'lerde ara
        for (const claim of refundProduct) {
          if (claim.items && Array.isArray(claim.items)) {
            const item = claim.items.find(i => i.order_product?.Id === product.Id);
            if (item && item.requested_quantity) {
              requestedQty = item.requested_quantity;
              break; // Bulunca dur
            }
          }
        }
        return {
          ...product,
          requested_quantity: requestedQty || product.Count || 0
        };
      });
      setFilteredProducts(items);
    } else {
      setFilteredProducts([]);
    }
  }, [refundProduct]);

  // Para formatlama
  const formatCurrency = (amount) =>
    new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);

  // Tarih formatlama
  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const dt = new Date(isoString);
    return dt.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // İlk shipment objesi
  const shipment = Array.isArray(refundShipment) && refundShipment[0];
  const hasBarcode = shipment && !!shipment.ShippingBarcode;

  return (
    <Container
      fluid
      className="d-flex flex-column justify-content-center align-items-center return-summary-container"
      style={{ minHeight: '100vh', padding: '1rem' }}
    >
      <Card className="summary-card-return-new">
        {/* Sipariş Numarası Header */}
        <div className="order-header">
          <p className="order-number-text">
            Sipariş Numarası: <span className="order-number">#{orderNumber}</span>
          </p>
        </div>

        {/* QR Kod veya Bilgi */}
        <div className="qr-section">
          {hasBarcode ? (
            <>
              <div className="qr-wrapper">
                <QRCodeSVG
                  value={shipment.ShippingBarcode}
                  size={180}
                  level="L"
                  includeMargin
                />
              </div>
              <h3 className="success-title">İade Kargo Barkodunuz Hazır!</h3>
              <p className="barcode-text">
                <strong>{shipment.ShippingBarcode}</strong>
              </p>
              <p className="info-text">
                Size en yakın <strong>{shipment.CarrierName}</strong> şubesinden 7 gün içinde iade kargonuzu gönderebilirsiniz.
              </p>
              <p className="date-text">
                Oluşturulma: {formatDateTime(shipment.LabelCreatedAt)}
              </p>
            </>
          ) : (
            <>
              <div className="info-icon-wrapper">
                <BsInfoCircle size={56} className="info-icon" />
              </div>
              <h3 className="success-title">İade Talebinizi Aldık</h3>
                <p className="info-text">
                  İade talebinizi aldık ve şu anda inceliyoruz. Süreç tamamlandığında sizi bilgilendireceğiz. İade kargo gönderim barkodunuz tarafınıza e‑mail olarak iletilecektir.
                </p>
            </>
          )} 
        </div>

        {/* İade Edilen Ürünler */}
        <div className="products-section">
          <h4 className="section-title">
            <RiShoppingBasketLine className="section-icon" />
            İade Edilen Ürünler ({filteredProducts.length})
          </h4>
          <div className="products-list">
            {filteredProducts.map((p) => (
              <div key={p.Id} className="product-item">
                <Image
                  src={p.image}
                  alt={p.Name}
                  className="product-image-new"
                />
                <div className="product-details">
                  <h5 className="product-name">{p.Name}</h5>
                  <div className="product-meta">
                    <span className="meta-item">Adet: {p.Count}</span>
                    {p.requested_quantity && (
                      <span className="meta-item requested">Talep: {p.requested_quantity}</span>
                    )}
                    <span className="product-price">{formatCurrency(p.BasePrice)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Destek Bilgisi */}
        <div className="support-section">
          <MdOutlineContactSupport className="support-icon" />
          <span className="support-text">support@kolayiade.com</span>
        </div>
      </Card>
    </Container>
  );
};

export default ReturnSummary;
